"use client";

import React from "react";
import { Info, Check, Rocket, Building2, Crown, ShieldCheck, Layers3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const tiers = [{
  name: "Launch",
  description: "Start branded client delivery with core white-label capabilities.",
  price: "$499",
  period: "/mo",
  icon: Rocket,
  features: ["Up to 10 branded tenants", "Brand removal", "Tenant branding", "Subdomain routing", "Branded email templates"],
  ctaText: "Start White-Label",
  ctaLink: "/BookADemoContactSalesPage",
  ctaSubtext: "Requires Business + Capacity Pack",
  highlighted: false
}, {
  name: "Scale",
  description: "For agencies growing branded delivery across more client environments.",
  price: "$1,199",
  period: "/mo",
  icon: Building2,
  features: ["Up to 50 branded tenants", "Everything in Launch", "Agency presets", "Priority support lane"],
  ctaText: "Start Scale",
  ctaLink: "/BookADemoContactSalesPage",
  ctaSubtext: "Requires Business + Capacity Pack",
  highlighted: true
}, {
  name: "Elite",
  description: "Sales-led white-label setup for larger agency operations and advanced delivery needs.",
  price: "$2,499",
  period: "/mo",
  icon: Crown,
  features: ["Up to 200 branded tenants", "Advanced agency operations support", "Sales-led onboarding", "Custom rollout support"],
  ctaText: "Talk to Sales",
  ctaLink: "/BookADemoContactSalesPage",
  ctaSubtext: "Requires Business + Capacity Pack",
  highlighted: false
}];
export default function WhiteLabelAddOn() {
  return <section id="white-label-add-on" className="relative overflow-hidden bg-background py-16 sm:py-20">
      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <Layers3 className="size-4" />
            White-label for agencies
          </div>

          <h2 className="text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
            Resell EloRAG under your own brand
          </h2>

          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            White-label is a separate monthly add-on for agencies and resellers that want branded
            RAG delivery with approved-only retrieval, citation-based answers, and structured
            multi-tenant control.
          </p>
        </div>

        <div className="mx-auto mt-10 max-w-5xl">
          <div className="rounded-2xl border border-primary/25 bg-primary/[0.045] p-6 shadow-[0_12px_30px_-15px_rgba(0,0,0,0.12)]">
            <div className="flex items-start gap-4">
              <div className="rounded-xl bg-primary/10 p-3 shrink-0">
                <Info className="size-5 text-primary" />
              </div>

              <div className="min-w-0">
                <div className="text-base font-semibold text-foreground">
                  Minimum reseller setup
                </div>

                <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                  White-label requires <strong className="text-foreground">Business plan or higher</strong>,
                  a <strong className="text-foreground">White-Label subscription</strong>, and at least
                  <strong className="text-foreground"> one Capacity Pack</strong>.
                </p>

                <div className="mt-4 inline-flex rounded-full border border-border/70 bg-background/80 px-4 py-2 text-sm font-semibold text-foreground">
                  Minimum reseller setup starts at $1,097/mo
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-14 grid max-w-md grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-3">
          {tiers.map((tier, i) => {
          const Icon = tier.icon;
          return <Card key={tier.name} data-index={i} className={`relative flex min-h-[560px] flex-col justify-between rounded-2xl border transition-all duration-200 ${tier.highlighted ? "border-primary ring-2 ring-primary/15 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.14)]" : "border-border/70 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.12)]"}`}>
                {tier.highlighted && <div className="absolute -top-4 left-0 right-0 mx-auto w-fit rounded-full bg-primary px-3 py-1 text-xs font-medium uppercase tracking-wider text-primary-foreground shadow-sm">
                    Most Popular for Agencies
                  </div>}

                <CardHeader>
                  <div className="mb-4 flex size-12 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="size-6 text-primary" />
                  </div>

                  <CardTitle className="text-2xl text-foreground">{tier.name}</CardTitle>
                  <CardDescription className="mt-2 min-h-[48px] text-sm leading-relaxed">
                    {tier.description}
                  </CardDescription>

                  <div className="mt-6 flex items-baseline gap-x-1">
                    <span className="text-4xl font-bold tracking-tight text-foreground">
                      {tier.price}
                    </span>
                    <span className="text-sm font-semibold leading-6 text-muted-foreground">
                      {tier.period}
                    </span>
                  </div>
                </CardHeader>

                <CardContent className="flex-1">
                  <ul className="space-y-3 text-sm leading-6 text-muted-foreground">
                    {tier.features.map((feature, index) => <li key={index} className="flex gap-x-3">
                        <Check className="mt-0.5 h-5 w-5 flex-none text-primary" />
                        <span>{feature}</span>
                      </li>)}
                  </ul>
                </CardContent>

                <CardFooter className="flex flex-col items-stretch gap-3">
                  <Button asChild variant={tier.highlighted ? "default" : "outline"} className="h-12 w-full text-base" size="lg">
                    <Link to={tier.ctaLink}>{tier.ctaText}</Link>
                  </Button>

                  <div className="text-center text-xs font-medium text-muted-foreground">
                    {tier.ctaSubtext}
                  </div>
                </CardFooter>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}